import { UIEntitiesRef, updateAllEntities, upsertEntities, upsertEntitiesById } from '@ngneat/elf-entities';
import { setPage, updatePaginationData } from '@ngneat/elf-pagination';
import { AxiosError, AxiosResponse } from 'axios';
import { Observable, catchError, from, map, switchMap, tap } from 'rxjs';

import APIAxios, { APIRoutes } from '@api/axios.api';
import { GridSortItem } from '@mui/x-data-grid-pro';
import { Pagination } from '@store/common/pagination.model';
import { TableFilter } from '@store/common/tableFilter.model';
import SnackError from '@utils/error.utils';
import { CreateTemplateDto, Templates, TemplatesFilters, TemplatesPagination } from './templates.model';
import {getAuditTemplateDetailsDataSource, getTemplatesDataSource} from './templates.requests';
import { templatesStore } from './templates.store';
import {templatesQuery} from '@store/audits/templates/templates.query';

export class TemplatesService {
  store = templatesStore;

  setSort = (sort: GridSortItem) => this.store.update((state) => ({ ...state, sort }));
  setPagination = (name: string) => (value: number) =>
    this.store.update((state) => ({
      ...state,
      tablePagination: {
        ...state.tablePagination,
        [name]: value,
      },
    }));

  resetFilters = () =>
    this.store.update((state) => ({
      ...state,
      filters: { search: '', tableFilters: TemplatesFilters.initTableFilters() },
    }));

  setFilters = (name: string) => (value: any) =>
    this.store.update((state) => ({
      ...state,
      filters: {
        ...state.filters,
        [name]: value,
      },
    }));

  getTemplates = (): Observable<TemplatesPagination> => {
    const {
      filters: { search, tableFilters },
      sort,
      tablePagination,
    } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(
      APIAxios({
        ...APIRoutes.GETAuditsTemplates(),
        params: {
          ...requestFilters,
          searchString: search || undefined,
          page: tablePagination.page,
          limit: tablePagination.limit,
          sortBy: sort.field,
          orderBy: sort.sort?.toLocaleUpperCase(),
        },
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<TemplatesPagination>) => {
        return response.data;
      }),
      tap((templatesPagination) => {
        const paginationData = Pagination.toPaginationData(templatesPagination, tablePagination.page);

        this.store.update(
          upsertEntities(templatesPagination.data),
          upsertEntitiesById(templatesPagination.data.map((template) => template.id), {
            updater: (templateUi) => templateUi,
            creator: (templateId) => ({ id: templateId, selected: false }),
            ref: UIEntitiesRef,
          }),
          updatePaginationData(paginationData),
          setPage(
            paginationData.currentPage,
            templatesPagination.data.map((template) => template.id)
          ),
          getTemplatesDataSource.setSuccess()
        );
      }),
    );
  };

  createTemplate = (data: CreateTemplateDto) => {
    return from(APIAxios({
      ...APIRoutes.POSTAuditsTemplates(),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Templates>) => {
          return response.data;
        }),
        switchMap(() => this.getTemplates()),
      );
  };

  setSelectedTemplates = (templatesIds: number[]) => {
    this.store.update(
      updateAllEntities(
        (templatesUI) => ({
          ...templatesUI,
          selected: templatesIds.includes(templatesUI.id)
        }),
        { ref: UIEntitiesRef },
      ),
    );
  };

  deleteTemplate = (templateId: number | string) => {
    return from(APIAxios(APIRoutes.DELETEAuditsTemplates(templateId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getTemplates()),
      );
  };

    getTemplateById = (templateId: number | string): Observable<Templates> => {
        return from(APIAxios(APIRoutes.GETAuditTemplateById(templateId)))
            .pipe(
                catchError((err: AxiosError) => {
                    throw new SnackError((err.response as any)?.data?.message, 'error');
                }),
                map((response: AxiosResponse<Templates>) => {
                    return response.data;
                }),
                tap((template) =>
                    this.store.update((state) => ({
                            ...state,
                            ...template,
                        }),
                        getAuditTemplateDetailsDataSource.setSuccess(),
                    )
                ),
                getAuditTemplateDetailsDataSource.trackRequestStatus(),
            );
    };

    deleteAuditTemplates() {
        const ids = templatesQuery.selectedTemplates;

        this.setSelectedTemplates([]);
        return from(
            APIAxios({
                ...APIRoutes.DELETESelectedAuditsTemplates(),
                data: {ids},
            })
        ).pipe(
            catchError((err: AxiosError) => {
                throw new SnackError((err.response as any)?.data?.message, 'error');
            }),
            switchMap(() => this.getTemplates().pipe(map(() => ids)))
        );
    }
}

export const templatesService = new TemplatesService();
